import debounce from 'debounce-promise';
import { useField } from 'formik';
import React from 'react';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import closeIcon from 'images/directory/x_icon.svg';

import { REACT_SELECT_STYLES } from '@utils/constants';
import { capitalize } from '@utils/formatting';
import { AutocompleteOption, AutocompleteRecord, CreateRecordResponse } from '@utils/interfaces';

import Pill from '../Pill';
import FieldError from './FieldError';
import FieldLabel from './FieldLabel';

const DEBOUNCE_MS = 500;

type Option = AutocompleteOption | AutocompleteRecord;
interface MultiValueAutocompleteFieldProps {
  className?: string;
  createMethod?: (value: string) => Promise<CreateRecordResponse>;
  defaultOptions?: (AutocompleteOption | AutocompleteRecord)[];
  fetchMethod?: (value: string) => Promise<Option[]>;
  inputLengthFetchThreshold?: number;
  isClearable?: boolean;
  isCreatable?: boolean;
  isDisabled?: boolean;
  label?: string;
  name: string;
  options?: Option[];
  placeholder?: string;
  showPills?: boolean;
}

export default function MultiValueAutocompleteField(props: MultiValueAutocompleteFieldProps) {
  const {
    createMethod,
    fetchMethod,
    inputLengthFetchThreshold = 2,
    isCreatable = true,
    isDisabled,
    label,
    name,
    options,
    showPills = true,
  } = props;
  const [field, meta, helpers] = useField(props);

  const SelectFieldComponent = isCreatable ? AsyncCreatableSelect : AsyncSelect;

  const asyncLoadOptions = async (value) => {
    let loadedOptions = options || [];
    if (value.length >= inputLengthFetchThreshold) {
      // if fetchMethod is present, use it, otherwise, just look at the options list
      loadedOptions = fetchMethod
        ? await fetchMethod(value)
        : options.filter((o) => o.label.toLowerCase().includes(value.toLowerCase()));
    }
    return loadedOptions.map((option) => ({
      label: option?.label || option,
      value: option?.value || option,
    }));
  };

  const debounceLoadOptions = debounce((value) => asyncLoadOptions(value), DEBOUNCE_MS, {
    leading: true,
  });

  const handleChange = (value) => {
    helpers.setValue(
      value || {
        label: '',
        value: '',
      }
    );
  };

  const onCreateOption = async (value) => {
    const result = await createMethod(value);
    handleChange([...field.value, { label: value, value: result.id }]);
  };

  const handleRemoveValue = (option) => {
    if (isDisabled) return;
    const list = field.value.filter((val) => val.value !== option.value);
    handleChange(list);
  };
  const onCreateProp = isCreatable
    ? {
        onCreateOption: createMethod && onCreateOption,
      }
    : {};

  return (
    <div
      className={`MultiValueAutocompleteField form-group ${
        meta.touched && meta.error ? 'is-invalid' : ''
      }`}
    >
      {label && <FieldLabel htmlFor={name}>{label}</FieldLabel>}
      <div className="forum-group-content">
        <div className="input-container">
          <SelectFieldComponent
            className={meta.touched && meta.error ? 'is-invalid' : ''}
            classNamePrefix="react-select"
            onChange={(value) => helpers.setValue(value)}
            onBlur={() => helpers.setTouched(true)}
            loadOptions={debounceLoadOptions}
            isClearable={false}
            isMulti={true}
            value={field.value}
            inputId={name}
            controlShouldRenderValue={!showPills}
            backspaceRemovesValue={false}
            styles={REACT_SELECT_STYLES}
            {...props}
            {...onCreateProp}
          />
          {/* meta.error is of type string, but in this case the error lives in an object of type AutocompleteOption, the following code does a force cast */}
          {meta.touched && meta.error && (
            <FieldError>{(meta.error as unknown as AutocompleteOption).value}</FieldError>
          )}
        </div>
        {showPills && (
          <div className="pill-container">
            {field.value &&
              field.value.map((val, id) => (
                <Pill
                  onClick={() => handleRemoveValue(val)}
                  /* capitalize first letter of each word (can't use lodash's startCase because it removes special characters) */
                  text={capitalize(val.label)}
                  icon={closeIcon}
                  key={id}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
